/* eslint-disable max-lines */
import { CbhIcon, Text, Title, type UseModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Checkbox, FormControlLabel, Stack } from "@mui/material";
import { WheelTimePicker } from "@src/appV2/redesign/components/WheelTimePicker/WheelTimePicker";
import { formatTimeWithTimezone } from "@src/appV2/redesign/utils/formatTimeWithTimezone";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import { Button } from "../../../components/Button";
import { useClockInTimeValidation } from "../../Timekeeping/hooks/useClockInTimeValidation";
import {
  getBottomSheetIntent,
  TimekeepingAdjustmentBottomSheet,
} from "../../Timekeeping/TimekeepingAdjustmentBottomSheet";
import { getDayIndicator } from "../../Timekeeping/Timesheet/utils/getDayIndicator";
import { inferDateFromTime } from "../../Timekeeping/Timesheet/utils/inferDateFromTime";
import {
  getLocalTimeForPicker,
  getUtcTimeFromPicker,
} from "../../Timekeeping/Timesheet/utils/timezoneConversion";

interface CommonProps {
  modalState: UseModalState;
  workplaceTimezone: string;
  shiftStartAt: Date;
  shiftEndAt: Date;
  existingReason?: string;
  /**
   * Time to pre-populate in the picker. Defaults to `originalTime` when not provided.
   * Use this when the worker has an existing adjustment and you want the picker to open
   * at their previously adjusted time rather than the true original.
   */
  initialTime?: Date;
  onConfirm: (newTime: Date, reason: string) => void;
  onCancel?: () => void;
}

interface ClockInProps extends CommonProps {
  type: "clock-in";
  originalTime: Date;
  shiftId?: string;
  geofenceArrivalTime?: Date;
  earliestPlausibleArrivalTime?: Date;
  workplaceSettings?: { preventEarlyClockIns: boolean; earlyClockInMinutes?: number };
  isEnforceGeofenceTimestampsEnabled?: boolean;
  verifiedClockIn?: Date;
  workerClockIn?: Date;
  clockOutTime?: Date;
  breakStartTime?: Date;
  isGeofenceWorkplace?: boolean;
  isNfcOrMeshWorkplace?: boolean;
}

interface ClockOutProps extends CommonProps {
  type: "clock-out";
  originalTime: Date;
}

interface BreakProps extends CommonProps {
  type: "break";
  originalTime: Date;
  workplaceName: string;
  breakPolicy: string;
  mandatoryBreakDurationMinutes: number;
  computeRequiresApproval: (newTime: Date) => boolean;
}

export type AdjustTimekeepingBottomSheetProps = ClockInProps | ClockOutProps | BreakProps;

const CONFIG = {
  "clock-in": {
    label: "Clock in",
    title: "Adjust clock in",
    iconType: "clock" as const,
    reasonTitle: "Why did you adjust your clock in time?",
    reasonHelperText:
      "Edit your message to the workplace to explain why you adjusted your clock in time.",
    intentContent:
      "You are requesting an earlier clock in time. No worries - you'll be able to add the reason on the next screen.",
  },
  "clock-out": {
    label: "Clock out",
    title: "Adjust clock out",
    iconType: "clock" as const,
    reasonTitle: "Why did you adjust your clock out time?",
    reasonHelperText:
      "Edit your message to the workplace to explain why you adjusted your clock out time.",
    intentContent:
      "You are requesting a later clock out time. No worries - you'll be able to add the reason on the next screen.",
  },
  break: {
    label: "Break",
    title: "Adjust break",
    iconType: "break" as const,
    reasonTitle: "Why did you adjust your break?",
    reasonHelperText: "Edit your message to the workplace to explain why you adjusted your break.",
    intentContent:
      "You are requesting a shorter break. No worries - you'll be able to add the reason on the next screen.",
  },
} as const;

export function AdjustTimekeepingBottomSheet({
  modalState,
  type,
  originalTime,
  workplaceTimezone,
  shiftStartAt,
  shiftEndAt,
  existingReason,
  initialTime,
  onConfirm,
  onCancel,
  ...rest
}: AdjustTimekeepingBottomSheetProps) {
  const {
    workplaceName,
    breakPolicy,
    mandatoryBreakDurationMinutes,
    computeRequiresApproval: computeBreakRequiresApproval,
  } = type === "break" ? (rest as BreakProps) : ({} as BreakProps);

  const clockInValidationProps = type === "clock-in" ? (rest as ClockInProps) : undefined;

  const config = CONFIG[type];

  const [isPickerScrolling, setIsPickerScrolling] = useState(false);
  const [policyAcknowledged, setPolicyAcknowledged] = useState(false);

  const computeRequiresApproval = (currentTime: Date): boolean => {
    if (type === "clock-in") {
      return currentTime < originalTime;
    }
    if (type === "clock-out") {
      return currentTime > originalTime;
    }
    return computeBreakRequiresApproval(currentTime);
  };

  const pickerSeedTime = initialTime ?? originalTime;

  const { setValue, watch, reset, formState } = useForm<{ time?: Date }>({
    defaultValues: {
      time: getLocalTimeForPicker(pickerSeedTime, workplaceTimezone),
    },
  });

  useEffect(() => {
    if (!modalState.modalIsOpen) {
      reset({ time: getLocalTimeForPicker(pickerSeedTime, workplaceTimezone) });
      setPolicyAcknowledged(false);
    }
  }, [modalState.modalIsOpen, pickerSeedTime, reset, workplaceTimezone]);

  const currentLocalTime = watch("time");
  const currentTime = isDefined(currentLocalTime)
    ? getUtcTimeFromPicker(currentLocalTime, workplaceTimezone)
    : undefined;

  const handleTimeChange = (newTime: Date | undefined) => {
    if (isDefined(newTime)) {
      const inferResult = inferDateFromTime({
        timeInput: { hour: newTime.getHours(), minute: newTime.getMinutes() },
        shiftStart: getLocalTimeForPicker(shiftStartAt, workplaceTimezone),
        shiftEnd: getLocalTimeForPicker(shiftEndAt, workplaceTimezone),
        earlyBufferMinutes: 30,
      });
      setValue("time", inferResult.success ? inferResult.date : newTime, { shouldDirty: true });
    }
  };

  const hasClockInValidation =
    isDefined(clockInValidationProps) && isDefined(clockInValidationProps.shiftId);

  const clockInValidation = useClockInTimeValidation({
    selectedTime: hasClockInValidation ? currentTime : undefined,
    shiftId: clockInValidationProps?.shiftId ?? "",
    shiftStartAt,
    shiftEndAt,
    workplaceTimezone,
    geofenceArrivalTime: clockInValidationProps?.geofenceArrivalTime,
    earliestPlausibleArrivalTime: clockInValidationProps?.earliestPlausibleArrivalTime,
    workplaceSettings: clockInValidationProps?.workplaceSettings ?? {
      preventEarlyClockIns: false,
    },
    isEnforceGeofenceTimestampsEnabled:
      clockInValidationProps?.isEnforceGeofenceTimestampsEnabled ?? false,
    verifiedClockIn: clockInValidationProps?.verifiedClockIn,
    workerClockIn: clockInValidationProps?.workerClockIn,
    clockOutTime: clockInValidationProps?.clockOutTime,
    breakStartTime: clockInValidationProps?.breakStartTime,
    isGeofenceWorkplace: clockInValidationProps?.isGeofenceWorkplace,
    isNfcOrMeshWorkplace: clockInValidationProps?.isNfcOrMeshWorkplace,
    suppressMissedPunchWarning: false,
  });

  const baseRequiresApproval =
    formState.isDirty && isDefined(currentTime) && computeRequiresApproval(currentTime);

  // For clock-in with validation: also require approval when missed punch warning fires
  const requiresApproval =
    baseRequiresApproval ||
    (hasClockInValidation &&
      isDefined(clockInValidation.intentContent) &&
      clockInValidation.isValid);

  const hasClockInValidationError =
    hasClockInValidation && isDefined(clockInValidation.intent) && !clockInValidation.isValid;

  const showIntent = (requiresApproval || hasClockInValidationError) && !isPickerScrolling;

  // Validation error takes priority, then validation info, then default approval info
  const intentProps = getIntentProps({
    hasClockInValidationError,
    hasClockInValidation,
    showIntent,
    clockInValidation,
    config,
  });

  const dayIndicatorReference = type === "clock-out" ? shiftEndAt : shiftStartAt;
  const dayIndicator = getDayIndicator({
    selectedTimestamp: currentTime,
    referenceTimestamp: dayIndicatorReference,
    workplaceTimezone,
  });

  const displayTime = isDefined(currentTime)
    ? formatTimeWithTimezone(currentTime, workplaceTimezone, false)
    : "-";

  return (
    <TimekeepingAdjustmentBottomSheet
      modalState={modalState}
      {...intentProps}
      pickerTitle={config.title}
      hasChanges={formState.isDirty}
      isPickerScrolling={isPickerScrolling}
      isPickerValid={
        isDefined(currentTime) &&
        (!hasClockInValidation ||
          (clockInValidation.isValid && !clockInValidation.isValidatingOverlap))
      }
      requiresApproval={requiresApproval}
      acknowledgementStepContent={
        type === "break" ? (
          <>
            <Title semibold variant="h4" component="h2">
              Break policy
            </Title>
            <Text variant="body2">
              {workplaceName} requires that you take a {mandatoryBreakDurationMinutes}
              -minute unpaid break. As a reminder, here is their mandatory break policy:
            </Text>
            <Text variant="body2" sx={{ fontStyle: "italic" }}>
              {breakPolicy}
            </Text>
            <FormControlLabel
              control={
                <Checkbox
                  checked={policyAcknowledged}
                  onChange={(event) => setPolicyAcknowledged(event.target.checked)}
                />
              }
              label={
                <Text variant="body2">
                  I acknowledge {workplaceName}&apos;s break policy, and my adjusted break is
                  consistent with this policy
                </Text>
              }
            />
          </>
        ) : undefined
      }
      isAcknowledgementStepComplete={policyAcknowledged}
      reasonTitle={config.reasonTitle}
      existingReason={existingReason}
      reasonHelperText={config.reasonHelperText}
      onPickerConfirm={
        hasClockInValidation ? async () => clockInValidation.validateOverlap() : undefined
      }
      onConfirm={(reason) => {
        if (isDefined(currentTime)) {
          onConfirm(currentTime, reason);
        }
      }}
      onCancel={onCancel}
    >
      <Stack direction="row" gap={2} alignItems="center" width="100%">
        <Box
          sx={{
            width: 30,
            height: 30,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "50%",
            backgroundColor: (theme) => theme.palette.background.paper,
          }}
        >
          <CbhIcon size="small" type={config.iconType} />
        </Box>
        <Text bold variant="body2" sx={{ flex: 1 }}>
          {config.label}
        </Text>
        <Button
          variant="outlined"
          size="small"
          sx={{
            flexShrink: 0,
            marginLeft: 2,
            backgroundColor: (theme) => theme.palette.common.white,
            pointerEvents: "none",
          }}
        >
          {displayTime}
        </Button>
      </Stack>

      <WheelTimePicker
        preventParentTouchStart
        value={currentLocalTime}
        dayIndicator={dayIndicator}
        containerSx={{ alignSelf: "center" }}
        onChange={handleTimeChange}
        onScrollingChange={setIsPickerScrolling}
      />
    </TimekeepingAdjustmentBottomSheet>
  );
}

interface IntentPropsParams {
  hasClockInValidationError: boolean;
  hasClockInValidation: boolean;
  showIntent: boolean;
  clockInValidation: ReturnType<typeof useClockInTimeValidation>;
  config: (typeof CONFIG)[keyof typeof CONFIG];
}

function getIntentProps(params: IntentPropsParams) {
  const { hasClockInValidationError, hasClockInValidation, showIntent, clockInValidation, config } =
    params;

  if (hasClockInValidationError) {
    return {
      intent: clockInValidation.intent,
      intentIcon: clockInValidation.intentIcon,
      intentContent: clockInValidation.intentContent,
    };
  }

  if (hasClockInValidation && showIntent && isDefined(clockInValidation.intentContent)) {
    return {
      intent: clockInValidation.intent,
      intentIcon: clockInValidation.intentIcon,
      intentContent: clockInValidation.intentContent,
    };
  }

  if (showIntent) {
    return {
      ...getBottomSheetIntent("info"),
      intentContent: config.intentContent as string,
    };
  }

  return { intent: undefined, intentIcon: undefined, intentContent: undefined };
}
/* eslint-enable max-lines */
